import React from 'react';
import { Check, RefreshCcw, Save } from 'lucide-react';

import PriceDistributionView from '../PriceDistributionView';
import { formatConfidencePercent } from '../../utils/formatters';

const conditionBadgeClass = (condition) => {
  if (condition === 'New') return 'bg-positive/10 text-positive';
  if (condition === 'Damaged' || condition === 'For Parts') return 'bg-danger/10 text-danger';
  if (condition === 'Unknown') return 'bg-surface-2 text-muted';
  return 'bg-warn/10 text-warn';
};

const sourceBadge = (priceSource) => {
  if (priceSource === 'ebay_rag') return { label: 'eBay RAG', className: 'bg-teal/10 text-teal' };
  if (priceSource === 'image') return { label: 'Image', className: 'bg-warn/10 text-warn' };
  return { label: 'Model Knowledge', className: 'bg-surface-2 text-muted' };
};

const HistoryGroupDesktopTable = ({
  group,
  originalItemsById,
  updatingId,
  updatingNameId,
  onOpenItemImage,
  onManualNameChange,
  onUpdateItemName,
  onPriceChange,
  onUpdatePrice,
}) => {
  return (
    <div className="hidden sm:block overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-surface border-b border-hairline">
            <th className="p-4 text-xs font-bold text-muted uppercase tracking-wider">AI Name</th>
            <th className="p-4 text-xs font-bold text-muted uppercase tracking-wider">Manual Name</th>
            <th className="p-4 text-xs font-bold text-muted uppercase tracking-wider text-right">Est. Value</th>
            <th className="p-4 text-xs font-bold text-muted uppercase tracking-wider">Confidence</th>
            <th className="p-4 text-xs font-bold text-muted uppercase tracking-wider">Actual Price</th>
            <th className="p-4 text-xs font-bold text-muted uppercase tracking-wider">Condition</th>
            <th className="p-4 text-xs font-bold text-muted uppercase tracking-wider">Price Source</th>
          </tr>
        </thead>
        <tbody>
          {group.items.map((item) => {
            const originalItem = originalItemsById[item.id];
            const isDirty = originalItem && parseFloat(originalItem.actual_price) !== parseFloat(item.actual_price);
            const isUpdating = updatingId === item.id;
            const isNameDirty = originalItem && (originalItem.manual_name || '') !== (item.manual_name || '');
            const isNameUpdating = updatingNameId === item.id;
            const condition = item?.condition || 'Unknown';
            const source = sourceBadge(item?.price_source || 'model_knowledge');

            return (
              <tr key={item.id} className="border-b border-hairline hover:bg-surface-2 transition-colors last:border-0">
                <td className="p-4 text-sm font-medium text-ink">
                  <button onClick={() => onOpenItemImage(item)} className="hover:text-accent hover:underline transition-colors text-left">
                    {item.item_name}
                  </button>
                </td>
                <td className="p-4 text-sm">
                  <div className="relative inline-flex items-center">
                    <input
                      type="text"
                      placeholder="Custom name..."
                      value={item.manual_name || ''}
                      onChange={(e) => onManualNameChange(item.id, e.target.value)}
                      className={`w-36 pr-8 py-1 px-2 border rounded text-sm text-ink focus:outline-none focus:ring-1 transition-all ${
                        isNameDirty ? 'border-accent focus:ring-accent bg-accent/5' : 'border-hairline focus:ring-positive bg-ink/[0.04]'
                      }`}
                    />
                    <button
                      onClick={() => onUpdateItemName(item.id, item.manual_name || '')}
                      disabled={!isNameDirty || isNameUpdating}
                      className={`absolute right-1 p-1 rounded transition-all ${
                        isNameUpdating ? 'text-muted' : (isNameDirty ? 'text-accent hover:bg-accent/10' : 'text-positive cursor-default')
                      }`}
                      title={isNameDirty ? 'Save Name' : 'Name Saved'}
                    >
                      {isNameUpdating ? <RefreshCcw size={14} className="animate-spin" /> : (isNameDirty ? <Save size={14} /> : <Check size={14} />)}
                    </button>
                  </div>
                </td>
                <td className="p-4 text-sm text-right">
                  <div className="text-positive font-bold">${item.estimated_value?.toLocaleString()}</div>
                  <PriceDistributionView
                    rangeValue={item?.estimated_value_range}
                    distributionValue={item?.price_estimate_distribution}
                    maxBins={3}
                    compact
                    className="mt-1 text-left"
                  />
                </td>
                <td className="p-4 text-sm text-ai font-semibold">{formatConfidencePercent(item?.confidence_score)}</td>
                <td className="p-4 text-sm">
                  <div className="relative inline-flex items-center">
                    <span className="absolute left-2 text-muted text-sm">$</span>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={item.actual_price}
                      onChange={(e) => onPriceChange(item.id, e.target.value)}
                      className={`w-24 pl-5 pr-8 py-1 border rounded text-sm text-ink focus:outline-none focus:ring-1 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                        isDirty ? 'border-accent focus:ring-accent bg-accent/5' : 'border-hairline focus:ring-positive bg-ink/[0.04]'
                      }`}
                      step="0.01"
                    />
                    <button
                      onClick={() => onUpdatePrice(item.id, item.actual_price)}
                      disabled={!isDirty || isUpdating}
                      className={`absolute right-1 p-1 rounded transition-all ${
                        isUpdating ? 'text-muted' : (isDirty ? 'text-accent hover:bg-accent/10' : 'text-positive cursor-default')
                      }`}
                      title={isDirty ? 'Save Price' : 'Price Saved'}
                    >
                      {isUpdating ? <RefreshCcw size={14} className="animate-spin" /> : (isDirty ? <Save size={14} /> : <Check size={14} />)}
                    </button>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${conditionBadgeClass(condition)}`}>
                    {condition}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold ${source.className}`}>
                    {source.label}
                  </span>
                </td>
              </tr>
            );
          })}

          {group.items.length >= 1 && (
            <tr className="bg-surface-2 border-t border-hairline">
              <td colSpan="2" className="p-4 text-xs font-bold text-muted uppercase tracking-wider text-right">Est. Sum</td>
              <td className="p-4 text-sm text-positive font-bold text-right">
                ${group.items.reduce((sum, i) => sum + (parseFloat(i.estimated_value) || 0), 0).toLocaleString()}
              </td>
              <td colSpan="4"></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default HistoryGroupDesktopTable;
